// src/features/battle/ComparisonView.tsx
import { CATEGORY_LABEL } from '../../core/constants';
import { useMemo, useState } from 'react';
import { useTeamStore } from '../../store/useTeamStore';
import { useScenarioStore } from '../../store/useScenarioStore';
import { calculateStats } from '../../core/stats';
import { calculateDamage } from '../../core/damage';
import { SPIRIT_MAP } from '../../data/mock-spirits';
import { SKILL_MAP } from '../../data/mock-skills';
import { NATURE_MAP } from '../../data/mock-natures';
import type { DamageResult, StatBlock, TeamSlot } from '../../core/types';

type Mode = 'skills' | 'defenders';

interface CompareRow {
  key: string;
  label: string;
  sublabel?: string;
  result: DamageResult;
  hp: number;
}

export function ComparisonView() {
  const team = useTeamStore((s) => s.team);
  const attackerIndex = useScenarioStore((s) => s.attackerIndex);
  const defenderIndex = useScenarioStore((s) => s.defenderIndex);
  const selectedSkillId = useScenarioStore((s) => s.selectedSkillId);
  const field = useScenarioStore((s) => s.field);

  const [mode, setMode] = useState<Mode>('skills');

  const attackerSlot = team.slots[attackerIndex];
  const defenderSlot = team.slots[defenderIndex];

  const rows = useMemo<CompareRow[]>(() => {
    if (!attackerSlot?.spiritId) return [];

    const attackerSpirit = SPIRIT_MAP[attackerSlot.spiritId];
    if (!attackerSpirit) return [];

    const attackerStats = calculateStats(
      attackerSlot.customBaseStats ?? attackerSpirit.baseStats,
      attackerSlot.ivs,
      attackerSlot.evs,
      NATURE_MAP[attackerSlot.nature],
      attackerSlot.level,
    );

    // 模式 1：按技能对比（攻击方所有技能 vs 当前防守方）
    if (mode === 'skills') {
      if (!defenderSlot?.spiritId) return [];
      const defenderSpirit = SPIRIT_MAP[defenderSlot.spiritId];
      if (!defenderSpirit) return [];

      const defenderStats = calculateStats(
        defenderSlot.customBaseStats ?? defenderSpirit.baseStats,
        defenderSlot.ivs,
        defenderSlot.evs,
        NATURE_MAP[defenderSlot.nature],
        defenderSlot.level,
      );

      return attackerSlot.skills
        .map((sid) => SKILL_MAP[sid])
        .filter(Boolean)
        .map((skill) => {
          const result = calculateDamage({
            attacker: {
              spirit: attackerSpirit,
              stats: attackerStats,
              skill,
              boosts: attackerSlot.boosts,
            },
            defender: {
              spirit: defenderSpirit,
              stats: defenderStats,
              boosts: defenderSlot.boosts,
            },
            field,
            level: attackerSlot.level,
          });
          return {
            key: skill.id,
            label: skill.name,
            sublabel: `威力 ${skill.power} · ${skill.type}`,
            result,
            hp: defenderStats.hp,
          };
        });
    }

    // 模式 2：按防守方对比（当前技能 vs 队伍中所有已配置的防守方）
    if (!selectedSkillId) return [];
    const skill = SKILL_MAP[selectedSkillId];
    if (!skill) return [];

    return team.slots
      .map((slot, idx) => ({ slot, idx }))
      .filter(({ slot }) => slot.spiritId && SPIRIT_MAP[slot.spiritId])
      .map(({ slot, idx }) => {
        const spirit = SPIRIT_MAP[slot.spiritId!];
        const stats = calculateStats(
          spirit.baseStats,
          slot.ivs,
          slot.evs,
          NATURE_MAP[slot.nature],
          slot.level,
        );
        const result = calculateDamage({
          attacker: {
            spirit: attackerSpirit,
            stats: attackerStats,
            skill,
            boosts: attackerSlot.boosts,
          },
          defender: {
            spirit,
            stats,
            boosts: slot.boosts,
          },
          field,
          level: attackerSlot.level,
        });
        return {
          key: `${idx}-${slot.spiritId}`,
          label: spirit.name,
          sublabel: `槽位 ${idx + 1} · Lv.${slot.level}`,
          result,
          hp: stats.hp,
        };
      });
  }, [
    mode,
    attackerSlot,
    defenderSlot,
    selectedSkillId,
    field,
    team.slots,
  ]);

  // 用于计算条形图百分比的最大值
  const maxPercent = Math.max(
    100,
    ...rows.map((r) => r.result.percentMax),
  );

  return (
    <div className="bg-slate-800 p-6 rounded-xl shadow-lg w-full max-w-2xl space-y-4">
      {/* 标题 + 模式切换 */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-lg font-semibold">结果对比</h3>
        <div className="flex gap-1 bg-slate-900 p-1 rounded-lg">
          <ModeButton
            active={mode === 'skills'}
            onClick={() => setMode('skills')}
          >
            按技能对比
          </ModeButton>
          <ModeButton
            active={mode === 'defenders'}
            onClick={() => setMode('defenders')}
          >
            按防守方对比
          </ModeButton>
        </div>
      </div>

      {/* 对比列表 */}
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500 py-6 text-center">
          {mode === 'skills'
            ? '请给攻击方配置技能，并选择防守方'
            : '请先选择技能，并配置至少一个防守方'}
        </p>
      ) : (
        <div className="space-y-2">
          {/* 表头 */}
          <div className="grid grid-cols-[1fr_60px_60px_60px_70px] gap-2 px-3 text-[10px] uppercase text-slate-500 tracking-wider">
            <span>名称</span>
            <span className="text-right">最小</span>
            <span className="text-right">最大</span>
            <span className="text-right">平均</span>
            <span className="text-right">击杀率</span>
          </div>

          {rows.map((row) => (
            <CompareRowItem
              key={row.key}
              row={row}
              maxPercent={maxPercent}
              highlight={row.result.koChance >= 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 text-xs rounded-md transition ${
        active
          ? 'bg-primary-600 text-white'
          : 'text-slate-400 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}

function CompareRowItem({
  row,
  maxPercent,
  highlight,
}: {
  row: CompareRow;
  maxPercent: number;
  highlight: boolean;
}) {
  const { result, label, sublabel, hp } = row;
  const widthPct = (result.percentMax / maxPercent) * 100;

  // 颜色：击杀 → 红，重伤 → 橙，中伤 → 黄，轻伤 → 蓝
  const barColor =
    result.koChance >= 1
      ? 'bg-red-500'
      : result.percentMax >= 50
        ? 'bg-orange-500'
        : result.percentMax >= 30
          ? 'bg-yellow-500'
          : 'bg-primary-500';

  return (
    <div
      className={`px-3 py-2.5 rounded-lg border transition ${
        highlight
          ? 'border-red-500/50 bg-red-500/5'
          : 'border-slate-700 bg-slate-900/50'
      }`}
    >
      <div className="grid grid-cols-[1fr_60px_60px_60px_70px] gap-2 items-center mb-2">
        <div className="min-w-0">
          <p className="text-sm text-white font-medium truncate">{label}</p>
          {sublabel && (
            <p className="text-[10px] text-slate-500 truncate">{sublabel}</p>
          )}
        </div>
        <span className="text-right text-xs font-mono text-slate-300">
          {result.min}
        </span>
        <span className="text-right text-xs font-mono text-slate-300">
          {result.max}
        </span>
        <span className="text-right text-xs font-mono text-slate-300">
          {result.avg}
        </span>
        <span
          className={`text-right text-xs font-mono ${
            result.koChance >= 1
              ? 'text-red-400'
              : result.koChance > 0
                ? 'text-yellow-400'
                : 'text-slate-500'
          }`}
        >
          {(result.koChance * 100).toFixed(0)}%
        </span>
      </div>

      {/* 条形图 */}
      <div className="relative h-2 bg-slate-800 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all`}
          style={{ width: `${widthPct}%` }}
        />
        {/* 50% 刻度线 */}
        <div
          className="absolute top-0 bottom-0 w-px bg-slate-600/60"
          style={{ left: `${(50 / maxPercent) * 100}%` }}
        />
      </div>

      <div className="flex justify-between mt-1 text-[10px] text-slate-500 font-mono">
        <span>
          {result.percentMin.toFixed(1)}% ~ {result.percentMax.toFixed(1)}%
        </span>
        <span>剩余 HP {result.remainingHp} / {hp}</span>
      </div>
    </div>
  );
}